/**
 * Permission utility functions
 * Check user permissions and determine role display
 */

/**
 * Check if user has any admin-level permissions
 * Admin permissions include wildcards for core resources
 *
 * @param permissions - Array of user permission strings
 * @returns True if user has admin/management permissions
 *
 * @example
 * ```typescript
 * hasAdminPermissions(['product:read', 'order:*'])  // true
 * hasAdminPermissions(['product:read'])              // false
 * ```
 */
export function hasAdminPermissions(permissions: string[]): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  const adminPatterns = [
    'admin:*',
    'product:*',
    'user:*',
    'order:*',
    'settings:*',
    'analytics:*',
    'audit:*',
    // Specific :admin action permissions
    'product:admin',
    'user:admin',
    'order:admin',
    'analytics:admin',
    'settings:admin',
    'audit:admin',
  ];

  return permissions.some(permission =>
    adminPatterns.includes(permission)
  );
}

/**
 * Get user role display name based on permissions
 * Returns a human-readable role badge for display in UI
 *
 * @param permissions - Array of user permission strings
 * @returns Display name for role badge or null if no special role
 *
 * @example
 * ```typescript
 * getUserRoleDisplay(['admin:*'])           // 'Admin'
 * getUserRoleDisplay(['product:*'])         // 'Manager'
 * getUserRoleDisplay(['product:read'])      // null
 * ```
 */
export function getUserRoleDisplay(permissions: string[]): string | null {
  if (!permissions || permissions.length === 0) {
    return null;
  }

  // Check for highest privilege first
  if (permissions.includes('admin:*')) {
    return 'Admin';
  }

  // Check for management-level permissions
  if (hasAdminPermissions(permissions)) {
    return 'Manager';
  }

  return null;
}

/**
 * Check if user has a specific permission
 * Supports wildcard matching (e.g., 'product:*' matches 'product:read')
 *
 * @param permissions - Array of user permission strings
 * @param requiredPermission - Permission to check for
 * @returns True if user has the required permission
 *
 * @example
 * ```typescript
 * hasPermission(['product:*'], 'product:read')       // true
 * hasPermission(['product:read'], 'product:update')  // false
 * ```
 */
export function hasPermission(
  permissions: string[],
  requiredPermission: string
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  const [requiredResource] = requiredPermission.split(':');

  return permissions.some(permission => {
    const [resource, action] = permission.split(':');

    // Exact match
    if (permission === requiredPermission) {
      return true;
    }

    // Wildcard match (e.g., 'product:*' matches 'product:read')
    if (resource === requiredResource && action === '*') {
      return true;
    }

    return false;
  });
}
